import React from 'react';
import { TFunction } from 'i18next';
import _get from 'lodash/get';
import { FieldItem } from '@lib/interfaces/form';
import Input from '@lib/components/Input/Input';
import TextEditor from '@lib/components/TextEditor/TextEditor';
import DepartmentFieldNames from '@lib/enums/fieldNames/departmentFieldNames';
import { MAX_TEXT_EDITOR_FIELD_LENGTH } from '@lib/enums/form';
import Select, {
  SelectComponents,
  SelectProps,
} from '@lib/components/Select/Select';
import { GetLoadOptions } from '@lib/components/Select/types';
import UserOption from '@lib/components/Select/components/UserOption';
import UserSingleValue from '@lib/components/Select/components/UserSingleValue';
import ChipOption from '@lib/components/Select/components/ChipOption';
import MultiSelectValueContainer from '@lib/components/Select/components/MultiSelectValueContainer';
import RadioButtonGroup, {
  RadioButtonWrapViews,
} from '@lib/components/RadioButtonGroup/RadioButtonGroup';
import RadioButtonAsChipColor from '@lib/components/RadioButtonAsChipColor/RadioButtonAsChipColor';
import getColorOptions from '@lib/components/Chip/utils/colorEnum';
import FieldTitleRender from '@lib/components/FieldTitleRender/FieldTitleRender';
import UserChipListField from '@lib/components/UserChipListField/UserChipListField';
import TeamChipListField from '@lib/components/TeamChipListField/TeamChipListField';
import {
  getFieldValidation,
  getOptionsArrayValidation,
  getOptionValidation,
} from '@lib/utils/validationUtils';
import DepartmentResultingHierarchy from '../components/DepartmentResultingHierarchy/DepartmentResultingHierarchy';

const multiSelectComponents = {
  ValueContainer: MultiSelectValueContainer,
} as unknown as SelectComponents;

type Args = {
  t: TFunction<'translation', undefined>;
  departmentId?: string;
  getDepartmentsLoadOptions: GetLoadOptions;
  getUsersLoadOptions: GetLoadOptions;
  getTeamsLoadOptions: GetLoadOptions;
  getInterventionsLoadOptions: GetLoadOptions;
  getTasksLoadOptions: GetLoadOptions;
  membersIds: { id: string }[];
  openTasksModal: () => void;
  openInterventionsModal: () => void;
  openDepartmentModal: () => void;
};

export default function getDepartmentFormFields(args: Args): FieldItem[] {
  const {
    t,
    departmentId,
    getDepartmentsLoadOptions,
    getUsersLoadOptions,
    getTeamsLoadOptions,
    getInterventionsLoadOptions,
    getTasksLoadOptions,
    membersIds,
    openTasksModal,
    openInterventionsModal,
    openDepartmentModal,
  } = args;

  return [
    // ── Basic information ──────────────────────────────────────────
    {
      name: DepartmentFieldNames.BasicInformationTitle,
      customRender: () => (
        <FieldTitleRender
          key="basic-information"
          title={t('basic-information')}
        />
      ),
    },
    {
      name: DepartmentFieldNames.Name,
      label: t('department-name'),
      element: Input,
      validation: getFieldValidation({ required: true, t }),
    },
    {
      name: DepartmentFieldNames.Description,
      label: t('description'),
      element: TextEditor,
      validation: getFieldValidation({
        maxLength: MAX_TEXT_EDITOR_FIELD_LENGTH,
        t,
      }),
    },
    {
      name: DepartmentFieldNames.Color,
      label: t('label-color'),
      element: RadioButtonGroup,
      validation: getOptionValidation({ required: true, t }),
      componentProps: {
        options: getColorOptions({ t }),
        RadioButtonComponent: RadioButtonAsChipColor,
        wrapView: RadioButtonWrapViews.colors,
      },
    },
    // ── Organization structure ────────────────────────────────────
    {
      name: DepartmentFieldNames.OrganizationStructureTitle,
      customRender: () => (
        <FieldTitleRender
          key="organization-structure"
          title={t('organization-structure')}
          isBlockStart
        />
      ),
    },
    {
      name: DepartmentFieldNames.ParentDepartment,
      label: t('parent-department'),
      element: Select,
      isOptionalFn: () => true,
      description: t('leave-empty-top-level-department'),
      componentProps: {
        isAsync: true,
        getLoadOptions: getDepartmentsLoadOptions,
        defaultOptions: false,
        isClearable: true,
        isSearchable: false,
        openMenuOnClick: false,
        openMenuOnFocus: false,
        menuIsOpen: false,
        onSelectWrapperMouseDown: openDepartmentModal,
      } as SelectProps,
    },
    {
      name: DepartmentFieldNames.ResultingHierarchy,
      isUseWatch: true,
      isVisibleFn: (values) =>
        !!_get(values, [DepartmentFieldNames.ParentDepartment, 'value']),
      customRender: (values) => (
        <DepartmentResultingHierarchy
          key="resulting-hierarchy"
          values={values}
        />
      ),
    },
    {
      name: DepartmentFieldNames.Manager,
      label: t('responsible-manager'),
      element: Select,
      isOptionalFn: () => true,
      componentProps: {
        isAsync: true,
        getLoadOptions: getUsersLoadOptions,
        defaultOptions: false,
        isClearable: true,
        maxMenuHeight: 200,
        usePortal: true,
        components: {
          Option: UserOption,
          SingleValue: UserSingleValue,
        } as unknown as SelectComponents,
      } as SelectProps,
    },
    // ── Members & access ──────────────────────────────────────────
    {
      name: DepartmentFieldNames.MembersAccessTitle,
      customRender: () => (
        <FieldTitleRender
          key="members-access"
          title={t('members-access')}
          isBlockStart
        />
      ),
    },
    {
      name: DepartmentFieldNames.Members,
      label: t('members'),
      element: Select,
      isOptionalFn: () => true,
      validation: getOptionsArrayValidation({ t }),
      componentProps: {
        isAsync: true,
        isMulti: true,
        getLoadOptions: getUsersLoadOptions,
        defaultOptions: false,
        components: {
          Option: UserOption,
          ValueContainer: MultiSelectValueContainer,
        } as unknown as SelectComponents,
      } as SelectProps,
      hideErrorsSpace: true,
    },
    {
      name: DepartmentFieldNames.MembersList,
      element: UserChipListField,
      componentProps: {
        fieldName: DepartmentFieldNames.Members,
        ids: membersIds,
        departmentId,
      },
      isUseWatch: true,
    },
    {
      name: DepartmentFieldNames.AccessGroups,
      label: t('linked-access-groups'),
      element: Select,
      isOptionalFn: () => true,
      validation: getOptionsArrayValidation({ t }),
      componentProps: {
        isAsync: true,
        isMulti: true,
        getLoadOptions: getTeamsLoadOptions,
        defaultOptions: false,
        components: {
          Option: ChipOption,
          ValueContainer: MultiSelectValueContainer,
        } as unknown as SelectComponents,
      } as SelectProps,
      hideErrorsSpace: true,
    },
    {
      name: DepartmentFieldNames.AccessGroupsList,
      element: TeamChipListField,
      componentProps: {
        fieldName: DepartmentFieldNames.AccessGroups,
        departmentId,
      },
      isUseWatch: true,
    },
    // ── Linked tasks ───────────────────────────────────────────────
    {
      name: DepartmentFieldNames.LinkedTasksTitle,
      customRender: () => (
        <FieldTitleRender
          key="linked-tasks"
          title={t('linked-tasks')}
          tooltipText={t('linked-tasks-department-tooltip')}
          isBlockStart
        />
      ),
    },
    {
      name: DepartmentFieldNames.Tasks,
      label: t('assign-tasks'),
      element: Select,
      isOptionalFn: () => true,
      componentProps: {
        isAsync: true,
        isMulti: true,
        getLoadOptions: getTasksLoadOptions,
        defaultOptions: false,
        isClearable: true,
        isSearchable: false,
        openMenuOnClick: false,
        openMenuOnFocus: false,
        menuIsOpen: false,
        onSelectWrapperMouseDown: openTasksModal,
        components: multiSelectComponents,
      } as SelectProps,
    },
    // ── Linked interventions ────────────────────────────────────────
    {
      name: DepartmentFieldNames.LinkedInterventionsTitle,
      customRender: () => (
        <FieldTitleRender
          key="linked-interventions"
          title={t('linked-interventions')}
          tooltipText={t('linked-interventions-department-tooltip')}
          isBlockStart
        />
      ),
    },
    {
      name: DepartmentFieldNames.Interventions,
      label: t('assign-interventions'),
      element: Select,
      isOptionalFn: () => true,
      componentProps: {
        isAsync: true,
        isMulti: true,
        getLoadOptions: getInterventionsLoadOptions,
        defaultOptions: false,
        isClearable: true,
        isSearchable: false,
        openMenuOnClick: false,
        openMenuOnFocus: false,
        menuIsOpen: false,
        onSelectWrapperMouseDown: openInterventionsModal,
        components: multiSelectComponents,
      } as SelectProps,
    },
  ];
}
